import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Button, Paper, Alert, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import { uploadInvoice } from '../services/api';
import { transformMindeeData } from '../utils/mindeeUtils';

const UploadPage = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles) => {
    // Accepter uniquement les fichiers PDF
    const pdfFile = acceptedFiles.find(file => file.type === 'application/pdf');
    if (pdfFile) {
      setFile(pdfFile);
      setError('');
    } else {
      setError('Veuillez sélectionner un fichier PDF valide.');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleUpload = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier PDF.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadInvoice(formData);
      
      // Transform the raw Mindee data into our application format
      const transformedData = transformMindeeData(response.extracted_data);
      
      navigate(`/form/${response.invoice_id}`, { 
        state: { extractedData: transformedData, rawMindeeData: response.extracted_data } 
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue lors du téléchargement de la facture.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Convertir une facture en Factur-X
      </Typography>
      
      <Typography variant="body1" paragraph align="center" sx={{ mb: 4 }}>
        Téléchargez votre facture PDF pour l'analyser et la convertir au format Factur-X
      </Typography>

      <Paper 
        elevation={3} 
        sx={{ 
          maxWidth: 600, 
          mx: 'auto', 
          p: 3,
          borderRadius: 2
        }}
      >
        <Box 
          {...getRootProps()} 
          className="dropzone"
          sx={{
            p: 5,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            borderRadius: 2,
            borderColor: isDragActive ? 'primary.main' : 'divider',
            bgcolor: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
          
          {isDragActive ? (
            <Typography variant="h6" align="center">
              Déposez votre fichier ici...
            </Typography>
          ) : (
            <>
              <Typography variant="h6" align="center" gutterBottom>
                Glissez-déposez votre facture PDF ici
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary">
                ou cliquez pour sélectionner un fichier
              </Typography>
            </>
          )}
        </Box>

        {file && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 2, 
              mb: 3, 
              bgcolor: 'rgba(25, 118, 210, 0.08)', 
              borderRadius: 1 
            }}
          >
            <DescriptionIcon color="primary" sx={{ mr: 2 }} />
            <Typography variant="body1" noWrap sx={{ flexGrow: 1 }}>
              {file.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(file.size / 1024).toFixed(0)} KB
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disabled={!file || loading}
          onClick={handleUpload}
          sx={{ py: 1.5 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Analyser et convertir'
          )}
        </Button>
      </Paper>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Les fichiers sont traités de manière sécurisée et supprimés après traitement.
        </Typography>
      </Box>
    </Box>
  );
};

export default UploadPage;
